import React from 'react';

import InlineSpinner from '../../shared/InlineSpinner';
import type { RearDeviceValidationUiState } from '../types/EventsOverviewTypes';

export type { RearDeviceValidationUiState };

type Props = {
  state: RearDeviceValidationUiState;
  title?: string;
  showLabel?: boolean;
};

export default function RearDeviceValidationBadge(props: Props): React.ReactElement {
  const { state, title, showLabel = false } = props;

  if (state === 'idle') {
    return (
      <span
        className={showLabel ? 'inline-flex min-h-5 min-w-[7rem] shrink-0' : 'inline-flex h-4 w-4 shrink-0'}
        aria-hidden
      />
    );
  }

  const labelClass = 'min-w-0 truncate text-[10px] font-semibold';

  if (state === 'loading') {
    return (
      <span
        className={[
          'inline-flex shrink-0 items-center gap-1.5 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-blue-700',
          'dark:border-blue-800/70 dark:bg-blue-950/40 dark:text-blue-200',
          showLabel ? 'max-w-[13rem]' : 'h-5 w-5 justify-center px-0',
        ].join(' ')}
        role="status"
        aria-label="Validating rear device"
        title={title || 'Checking rear device availability'}
      >
        <InlineSpinner size="xs" variant="accent" />
        {showLabel ? <span className={labelClass}>Checking rear device</span> : null}
      </span>
    );
  }

  if (state === 'pass') {
    return (
      <span
        className={[
          'inline-flex shrink-0 items-center gap-1.5 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-700',
          'dark:border-emerald-800/70 dark:bg-emerald-950/40 dark:text-emerald-200',
          showLabel ? 'max-w-[13rem]' : 'h-5 w-5 justify-center px-0',
        ].join(' ')}
        title={title || 'Rear device is ready to assign'}
        aria-label="Rear device validated"
      >
        <span className="text-sm font-bold" aria-hidden>✓</span>
        {showLabel ? <span className={labelClass}>Ready to assign</span> : null}
      </span>
    );
  }

  return (
    <span
      className={[
        'inline-flex shrink-0 items-center gap-1.5 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-red-700',
        'dark:border-red-800/70 dark:bg-red-950/40 dark:text-red-200',
        showLabel ? 'max-w-[16rem]' : 'h-5 w-5 justify-center px-0',
      ].join(' ')}
      title={title || 'Rear device validation failed'}
      aria-label="Rear device validation failed"
    >
      <span className="text-sm font-bold" aria-hidden>✕</span>
      {showLabel ? <span className={labelClass}>{title || 'Rear device issue'}</span> : null}
    </span>
  );
}
